import { useQuery } from '@tanstack/react-query';

export type ReleaseEntryKind = 'yeni' | 'duzeltildi' | 'hizlandi';

export interface ReleaseEntry {
  kind: ReleaseEntryKind;
  /** The part of the system, in the words the business uses. Null when the commit named none. */
  area: string | null;
  text: string;
  breaking: boolean;
}

export interface Release {
  /** The git tag, e.g. `v0.4.0`. */
  version: string;
  /** ISO date of the tagged commit. */
  date: string;
  entries: ReleaseEntry[];
}

/**
 * A static file, not an endpoint.
 *
 * The list is generated from the git history at release time
 * (`scripts/build-release-notes.mjs`) and baked into the web image, so it is a
 * sibling of the bundle that shipped with it — the notes and the code they
 * describe cannot be a version apart. That is the whole reason there is no API
 * here: an endpoint would serve the API image's idea of the release to the web
 * image's idea of the app, and during a blue-green swap those are briefly two
 * different things.
 *
 * Absent in development, on purpose. `pnpm notes:build` writes it when you want
 * to look at it; without it every surface below simply says there is nothing to
 * show, rather than an error nobody can act on.
 */
export const releaseNotesKey = ['release-notes'] as const;

export function useReleaseNotes() {
  return useQuery({
    queryKey: releaseNotesKey,
    // It cannot change without a new bundle, and a new bundle means a reload.
    staleTime: Infinity,
    gcTime: Infinity,
    // A missing file is an answer, not a failure to retry.
    retry: false,
    queryFn: async (): Promise<Release[]> => {
      // `no-cache` revalidates rather than trusting a heuristic freshness
      // lifetime: the file has no content hash in its name, so a browser is
      // otherwise free to keep yesterday's copy for as long as it likes.
      const res = await fetch('/release-notes.json', { cache: 'no-cache' });
      if (!res.ok) return [];
      const body: unknown = await res.json();
      const releases = (body as { releases?: unknown })?.releases;
      return Array.isArray(releases) ? (releases as Release[]) : [];
    },
  });
}

export const KIND_LABEL: Record<ReleaseEntryKind, string> = {
  yeni: 'Yeni',
  duzeltildi: 'Düzeltildi',
  hizlandi: 'Hızlandı',
};

/**
 * Four tones, and neutral is spoken for.
 *
 * `hizlandi` was neutral grey, which put an improvement in the same colour as
 * "nothing user-visible shipped" — the one row on this screen that means the
 * opposite of news. Amber separates them, and leaves grey to say "infrastructure".
 */
export const KIND_TONE: Record<ReleaseEntryKind, 'success' | 'primary' | 'warning'> = {
  yeni: 'primary',
  duzeltildi: 'success',
  hizlandi: 'warning',
};
